import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Download, Share2 } from 'lucide-react';

const W = 1080;
const H = 1350;

const STARS = Array.from({ length: 60 }, (_, i) => ({
  x: ((i * 137) % 100) / 100,
  y: ((i * 89 + 31) % 100) / 100,
  r: 1 + (i % 3),
  o: 0.3 + ((i * 7) % 6) / 10,
}));

const drawZenerSymbols = (ctx, cy, color) => {
  const size = 34;
  const gap = 130;
  const startX = W / 2 - gap * 2;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.globalAlpha = 0.55;
  // Circle
  ctx.beginPath();
  ctx.arc(startX, cy, size, 0, Math.PI * 2);
  ctx.stroke();
  // Plus
  ctx.beginPath();
  ctx.moveTo(startX + gap - size, cy); ctx.lineTo(startX + gap + size, cy);
  ctx.moveTo(startX + gap, cy - size); ctx.lineTo(startX + gap, cy + size);
  ctx.stroke();
  // Waves
  for (let w = 0; w < 3; w++) {
    ctx.beginPath();
    const wy = cy - 18 + w * 18;
    ctx.moveTo(startX + gap * 2 - size, wy);
    ctx.quadraticCurveTo(startX + gap * 2 - size / 2, wy - 14, startX + gap * 2, wy);
    ctx.quadraticCurveTo(startX + gap * 2 + size / 2, wy + 14, startX + gap * 2 + size, wy);
    ctx.stroke();
  }
  // Square
  ctx.strokeRect(startX + gap * 3 - size, cy - size, size * 2, size * 2);
  // Star
  ctx.beginPath();
  for (let p = 0; p < 5; p++) {
    const a = -Math.PI / 2 + (p * 4 * Math.PI) / 5;
    const px = startX + gap * 4 + Math.cos(a) * size * 1.15;
    const py = cy + Math.sin(a) * size * 1.15;
    if (p === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
};

const renderCard = (canvas, { accuracy, level, playerName, hits, total, bestStreak }) => {
  const ctx = canvas.getContext('2d');
  canvas.width = W;
  canvas.height = H;

  const bg = ctx.createRadialGradient(W / 2, H * 0.38, 100, W / 2, H / 2, H);
  bg.addColorStop(0, '#12101f');
  bg.addColorStop(0.5, '#0a0912');
  bg.addColorStop(1, '#050508');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  STARS.forEach((s) => {
    ctx.globalAlpha = s.o;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(s.x * W, s.y * H, s.r * 0.8, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = 1;

  // Aura ring behind score
  const ring = ctx.createRadialGradient(W / 2, 560, 80, W / 2, 560, 340);
  ring.addColorStop(0, `${level.color}30`);
  ring.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = ring;
  ctx.fillRect(0, 220, W, 700);

  // Border frame
  ctx.strokeStyle = `${level.color}55`;
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = 'center';

  // Brand
  ctx.fillStyle = '#f1f5f9';
  ctx.font = '600 76px "Playfair Display", serif';
  ctx.fillText('ZENER', W / 2, 180);
  ctx.fillStyle = '#c4b5fd';
  ctx.font = '600 58px "Playfair Display", serif';
  ctx.fillText('MIND', W / 2, 250);

  if (playerName) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '400 40px "Cormorant Garamond", serif';
    ctx.fillText(playerName, W / 2, 330);
  }

  // Score
  ctx.shadowColor = level.color;
  ctx.shadowBlur = 60;
  ctx.fillStyle = level.color;
  ctx.font = '700 210px "Playfair Display", serif';
  ctx.fillText(`%${accuracy}`, W / 2, 640);
  ctx.shadowBlur = 0;

  ctx.fillStyle = '#64748b';
  ctx.font = '400 34px Manrope, sans-serif';
  ctx.fillText('İ S A B E T   O R A N I', W / 2, 710);

  // Level
  ctx.fillStyle = '#94a3b8';
  ctx.font = '400 32px Manrope, sans-serif';
  ctx.fillText(`PSİŞİK SEVİYE ${level.level}`, W / 2, 810);
  ctx.shadowColor = level.color;
  ctx.shadowBlur = 30;
  ctx.fillStyle = level.color;
  ctx.font = '600 84px "Cinzel", serif';
  ctx.fillText(level.name, W / 2, 905);
  ctx.shadowBlur = 0;

  // Stats row
  const stats = [
    { label: 'İSABET', value: `${hits}/${total}` },
    { label: 'EN İYİ SERİ', value: `${bestStreak}` },
  ];
  const statY = 1030;
  stats.forEach((s, i) => {
    const sx = W / 2 + (i === 0 ? -180 : 180);
    ctx.fillStyle = '#e2e8f0';
    ctx.font = '600 64px "Playfair Display", serif';
    ctx.fillText(s.value, sx, statY);
    ctx.fillStyle = '#64748b';
    ctx.font = '400 26px Manrope, sans-serif';
    ctx.fillText(s.label, sx, statY + 46);
  });
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2, statY - 55);
  ctx.lineTo(W / 2, statY + 50);
  ctx.stroke();

  drawZenerSymbols(ctx, 1180, level.color);

  // Footer
  ctx.fillStyle = '#94a3b8';
  ctx.font = '500 34px Manrope, sans-serif';
  ctx.fillText('zenermind.app', W / 2, 1275);
  ctx.fillStyle = '#475569';
  ctx.font = '400 26px Manrope, sans-serif';
  ctx.fillText('Sen de sezgisel yeteneklerini test et', W / 2, 1315);
};

export const ShareResultCard = ({ session, psychicLevel, accuracy }) => {
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [canShare, setCanShare] = useState(false);

  const data = {
    accuracy,
    level: psychicLevel,
    playerName: (session?.playerName || '').slice(0, 24),
    hits: session?.hits || 0,
    total: session?.total || 0,
    bestStreak: session?.bestStreak || 0,
  };

  useEffect(() => {
    let active = true;
    const draw = () => {
      if (!active || !canvasRef.current) return;
      renderCard(canvasRef.current, data);
      setPreviewUrl(canvasRef.current.toDataURL('image/png'));
    };
    if (document.fonts?.ready) {
      document.fonts.ready.then(draw);
    } else {
      draw();
    }
    setCanShare(typeof navigator !== 'undefined' && !!navigator.canShare);
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, accuracy, psychicLevel]);

  const getBlob = useCallback(() => new Promise((resolve) => {
    canvasRef.current.toBlob(resolve, 'image/png');
  }), []);

  const handleDownload = useCallback(async () => {
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'zener-mind-sonuc.png';
    a.click();
    URL.revokeObjectURL(url);
  }, [getBlob]);

  const handleShare = useCallback(async () => {
    const blob = await getBlob();
    if (!blob) return;
    const file = new File([blob], 'zener-mind-sonuc.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Zener Mind Sonucum',
          text: `Zener Mind testinde %${accuracy} isabet ile "${psychicLevel.name}" seviyesine ulaştım! Sen de dene: zenermind.app`,
        });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    handleDownload();
  }, [getBlob, accuracy, psychicLevel, handleDownload]);

  return (
    <div className="glass-panel p-6 space-y-4 border-violet-500/20" data-testid="share-result-panel">
      <div className="flex items-center gap-2 text-violet-300">
        <Share2 className="w-4 h-4" />
        <span className="text-xs tracking-widest uppercase font-medium">Sonucunu Paylaş</span>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="w-36 flex-shrink-0 rounded-lg overflow-hidden border border-white/10 shadow-lg">
          {previewUrl ? (
            <img src={previewUrl} alt="Sonuç kartı önizleme" className="w-full" data-testid="share-card-preview" />
          ) : (
            <div className="w-full aspect-[4/5] bg-white/5 animate-pulse" />
          )}
        </div>
        <div className="flex-1 space-y-4 text-center sm:text-left">
          <p className="text-sm text-slate-400 leading-relaxed">
            Sonucunu şık bir görsel olarak indir veya sosyal medyada arkadaşlarınla paylaş.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center sm:justify-start">
            <button
              onClick={handleDownload}
              className="rounded-full bg-violet-500/20 px-6 py-3 text-sm font-medium text-violet-300 transition-all hover:bg-violet-500/30 border border-violet-500/30 flex items-center justify-center gap-2 active:scale-95"
              data-testid="download-result-card-btn"
            >
              <Download className="w-4 h-4" />
              Görseli İndir
            </button>
            {canShare && (
              <button
                onClick={handleShare}
                className="rounded-full bg-cyan-500/20 px-6 py-3 text-sm font-medium text-cyan-300 transition-all hover:bg-cyan-500/30 border border-cyan-500/30 flex items-center justify-center gap-2 active:scale-95"
                data-testid="share-result-card-btn"
              >
                <Share2 className="w-4 h-4" />
                Paylaş
              </button>
            )}
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
